import { useState } from 'react';
import { ChevronDown, Calendar, TrendingUp, Eye } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import { useProfitSettings } from '@/hooks/use-profit-settings';
import { calculateItemsProfitBatch } from '@/lib/profit-calculation';
import { useLocation } from '@/contexts/LocationContext';

interface SaleRecord {
  id: string;
  date: string;
  kategori: string;
  adet: number;
  ilkMaliyet: number;
  guncelMaliyet: number;
  satisFiyat: number;
  kar: number;
  nakit: number;
  krediKarti: number;
  senet: number;
  odero: number;
  paro: number;
  status: string;
}

interface DatabaseSale {
  id: string;
  sale_datetime: string;
  total_net: number;
  total_gross: number;
  total_discount: number;
  status: string;
  sale_items: {
    id: string;
    quantity: number;
    sale_price_each: number;
    cost_each: number;
    line_total_net: number;
    discount: number;
    product: {
      name: string;
      category: string;
      current_cost: number;
      initial_cost: number;
      item_code: string | null;
    };
  }[];
  sale_payments: {
    payment_type: string;
    amount: number;
  }[];
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const Last5DaysSales = () => {
  const { companyId, loading: authLoading } = useAuth();
  const { currentLocation } = useLocation();
  const { settings: profitSettings } = useProfitSettings();

  const { data: salesData = [], isLoading } = useQuery({
    queryKey: ['last5DaysSales', companyId, currentLocation?.id],
    queryFn: async (): Promise<DatabaseSale[]> => {
      if (!companyId) return [];
      
      const fiveDaysAgo = new Date();
      fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);
      
      // Fetch converted onhold records to identify sales linked to onhold conversions
      let convertedOnHoldsQuery = supabase
        .from('on_holds')
        .select('linked_sale_id')
        .eq('company_id', companyId)
        .eq('status', 'converted')
        .not('linked_sale_id', 'is', null);

      if (currentLocation?.id) {
        convertedOnHoldsQuery = convertedOnHoldsQuery.eq('location_id', currentLocation.id);
      }

      const { data: convertedOnHolds } = await convertedOnHoldsQuery;
      
      const onHoldLinkedSaleIds = new Set(
        (convertedOnHolds || [])
          .map((onHold: any) => onHold.linked_sale_id)
          .filter(Boolean)
      );
      
      let salesQuery = supabase
        .from('sales')
        .select(`
          id,
          sale_datetime,
          total_net,
          total_gross,
          total_discount,
          status,
          sale_items (
            id,
            quantity,
            sale_price_each,
            cost_each,
            line_total_net,
            discount,
            product:products (
              name,
              category,
              current_cost,
              initial_cost,
              item_code
            )
          ),
          sale_payments (
            payment_type,
            amount
          )
        `)
        .eq('company_id', companyId)
        .in('status', ['completed', 'open'])
        .gte('sale_datetime', fiveDaysAgo.toISOString());

      if (currentLocation?.id) {
        salesQuery = salesQuery.eq('location_id', currentLocation.id);
      }

      const { data, error } = await salesQuery.order('sale_datetime', { ascending: false });
      
      if (error) {
        console.error('Error fetching sales data:', error);
        return [];
      }
      
      // Filter out 0 TL sales that are linked to onhold conversions
      return (data || []).filter((sale) => {
        const isZeroAmount = Math.abs(sale.total_net || 0) < 0.01;
        const isLinkedToOnHold = onHoldLinkedSaleIds.has(sale.id);
        return !(isZeroAmount && isLinkedToOnHold);
      });
    },
    enabled: !!companyId && !authLoading,
  });

  const transformSalesData = async (sales: DatabaseSale[]): Promise<SaleRecord[]> => {
    // Prepare items for batch profit calculation with Mars prices
    // Track mapping from (saleId, itemIndex) to profit result index
    const profitItems: Array<{
      sku: string | null | undefined;
      saleDate: Date | string;
      quantity: number;
      revenue: number;
      cost_each?: number;
      current_cost?: number;
    }> = [];
    
    const itemToProfitIndexMap = new Map<string, number>();
    let globalItemIndex = 0;
    
    // Build profit items array and mapping in the same order
    sales.forEach(sale => {
      sale.sale_items.forEach((item, itemIndexInSale) => {
        const mapKey = `${sale.id}-${itemIndexInSale}`;
        itemToProfitIndexMap.set(mapKey, globalItemIndex++);
        
        profitItems.push({
          sku: item.product?.item_code || null,
          productName: item.product?.name || null,
          saleDate: sale.sale_datetime,
          quantity: item.quantity,
          revenue: item.line_total_net,
          cost_each: item.cost_each,
          current_cost: item.product?.current_cost,
        });
      });
    });

    // Calculate profits (with or without Mars prices)
    const profitResults = profitSettings.enabled && profitSettings.marsPriceTier
      ? await calculateItemsProfitBatch(profitItems, profitSettings.marsPriceTier, true)
      : await calculateItemsProfitBatch(profitItems, null, false);

    // Process results
    return sales.flatMap(sale => {
      // Group sale items by category, preserving item index for mapping
      const categoryGroups = sale.sale_items.reduce((acc, item, itemIndexInSale) => {
        const category = item.product?.category || 'Diğer';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push({ item, itemIndexInSale });
        return acc;
      }, {} as Record<string, Array<{ item: typeof sale.sale_items[0]; itemIndexInSale: number }>>);

      // Create a record for each category
      return Object.entries(categoryGroups).map(([category, itemsWithIndex]) => {
        const totalQuantity = itemsWithIndex.reduce((sum, { item }) => sum + item.quantity, 0);
        const totalSales = itemsWithIndex.reduce((sum, { item }) => sum + item.line_total_net, 0);
        const totalInitialCost = itemsWithIndex.reduce((sum, { item }) => sum + (item.product?.initial_cost || 0) * item.quantity, 0);
        
        // Calculate total cost using Mars prices if enabled
        // Map each item back to its profit result using the original index
        let totalCurrentCost = 0;
        itemsWithIndex.forEach(({ item, itemIndexInSale }) => {
          const mapKey = `${sale.id}-${itemIndexInSale}`;
          const profitIndex = itemToProfitIndexMap.get(mapKey);
          if (profitIndex !== undefined && profitResults[profitIndex]) {
            totalCurrentCost += profitResults[profitIndex].cost;
          } else {
            // Fallback to regular cost calculation if mapping fails
            const cost = item.cost_each || item.product?.current_cost || 0;
            totalCurrentCost += cost * item.quantity;
          }
        });
        
        const profit = totalSales - totalCurrentCost;

        // Calculate payment methods from sale level (proportionally distributed)
        const categoryRatio = totalSales / sale.total_net;
        const payments = sale.sale_payments.reduce((acc, payment) => {
          const amount = payment.amount * categoryRatio;
          switch (payment.payment_type) {
            case 'cash':
              acc.nakit += amount;
              break;
            case 'credit_card':
              acc.krediKarti += amount;
              break;
            case 'promissory':
              acc.senet += amount;
              break;
            case 'odero':
              acc.odero += amount;
              break;
            case 'paro':
              acc.paro += amount;
              break;
          }
          return acc;
        }, { nakit: 0, krediKarti: 0, senet: 0, odero: 0, paro: 0 });

        return {
          id: `${sale.id}-${category}`,
          date: sale.sale_datetime.split('T')[0],
          kategori: category,
          adet: totalQuantity,
          ilkMaliyet: totalInitialCost,
          guncelMaliyet: totalCurrentCost,
          satisFiyat: totalSales,
          kar: profit,
          status: sale.status,
          ...payments,
        };
      });
    });
  };

  const { data: salesRecords = [] } = useQuery({
    queryKey: ['last5DaysSalesTransformed', salesData, profitSettings.enabled, profitSettings.marsPriceTier],
    queryFn: () => transformSalesData(salesData),
    enabled: salesData.length > 0,
  });
  
  const totalSales = salesRecords.reduce((sum, record) => sum + record.satisFiyat, 0);
  const totalProfit = salesRecords.reduce((sum, record) => sum + record.kar, 0);
  const totalItems = salesRecords.reduce((sum, record) => sum + record.adet, 0);

  // Group sales by date
  const groupedByDate = salesRecords.reduce((acc, record) => {
    if (!acc[record.date]) {
      acc[record.date] = [];
    }
    acc[record.date].push(record);
    return acc;
  }, {} as Record<string, SaleRecord[]>);

  // Calculate daily totals
  const dailyTotals = Object.entries(groupedByDate).map(([date, records]) => ({
    date,
    totalSales: records.reduce((sum, record) => sum + record.satisFiyat, 0),
    totalProfit: records.reduce((sum, record) => sum + record.kar, 0),
    totalItems: records.reduce((sum, record) => sum + record.adet, 0),
    records
  })).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  if (authLoading || isLoading) {
    return (
      <Card className="glass-panel">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-xl" />
            <div>
              <Skeleton className="h-6 w-32 mb-2" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-6 w-16" />
          </div>
        </CardHeader>
      </Card>
    );
  }

  // Show empty state if no data
  if (salesRecords.length === 0) {
    return (
      <Card className="glass-panel hover-lift">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl glass-panel flex items-center justify-center">
              <Calendar className="h-5 w-5 text-accent" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold">Son 5 Günün Satışları</CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                Detaylı satış analizi ve ödeme yöntemleri
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Son 5 günde satış bulunamadı</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-panel border-border/30 bg-background/40">
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="last-5-days" className="border-0">
          <AccordionTrigger className="hover:no-underline px-3 sm:px-6 pt-4 sm:pt-6 pb-2">
            <CardHeader className="p-0 flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full pr-2 sm:pr-4 gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-muted/20 flex items-center justify-center">
                    <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                  </div>
                  <div className="text-left">
                    <CardTitle className="text-base sm:text-lg font-medium text-muted-foreground">Son 5 Günün Satışları</CardTitle>
                    <CardDescription className="text-xs sm:text-sm text-muted-foreground/70 hidden sm:block">
                      Detaylı satış analizi ve ödeme yöntemleri
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-4">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-success" />
                    <div className="text-right">
                      <div className="text-xs sm:text-sm font-medium text-muted-foreground">{formatCurrency(totalSales)}</div>
                      <div className="text-xs text-muted-foreground/70 hidden sm:block">Toplam Satış</div>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs font-medium bg-muted/10 text-muted-foreground border-muted-foreground/30">
                    {totalItems} Ürün
                  </Badge>
                </div>
              </div>
            </CardHeader>
          </AccordionTrigger>
          
          <AccordionContent className="px-3 sm:px-6 pb-4 sm:pb-6">
            <div className="mt-2 sm:mt-4 space-y-4">
              {/* Mobile-friendly overview stats */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div className="text-center p-2 sm:p-4 bg-muted/10 border border-border/30 rounded-lg">
                  <div className="text-lg sm:text-xl font-medium text-muted-foreground">{totalItems}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground/70">Toplam Adet</div>
                </div>
                <div className="text-center p-2 sm:p-4 bg-muted/10 border border-border/30 rounded-lg">
                  <div className="text-lg sm:text-xl font-medium text-muted-foreground">{formatCurrency(totalSales)}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground/70">Toplam Satış</div>
                </div>
                <div className="text-center p-2 sm:p-4 bg-muted/10 border border-border/30 rounded-lg">
                  <div className="text-lg sm:text-xl font-medium text-success/70">{formatCurrency(totalProfit)}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground/70">Toplam Kar</div>
                </div>
              </div>

              {/* Daily breakdown with nested accordions */}
              <div className="space-y-2">
                <Accordion type="single" collapsible className="w-full">
                  {dailyTotals.map((dayData) => (
                    <AccordionItem key={dayData.date} value={dayData.date} className="border glass-panel rounded-lg px-3 sm:px-4 py-2">
                      <AccordionTrigger className="hover:no-underline py-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full pr-2 gap-2">
                          <div className="flex items-center gap-3">
                            <div className="text-left">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-sm sm:text-base">{formatDate(dayData.date)}</span>
                                {dayData.records.some(r => r.status === 'open') && (
                                  <Badge variant="outline" className="text-xs bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
                                    Beklemede
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground">{dayData.totalItems} ürün</div>
                            </div>
                          </div>
                          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 text-right">
                            <div>
                              <div className="text-xs sm:text-sm font-medium">{formatCurrency(dayData.totalSales)}</div>
                              <div className="text-xs text-muted-foreground">Satış</div>
                            </div>
                            <div>
                              <div className="text-xs sm:text-sm font-medium text-success">{formatCurrency(dayData.totalProfit)}</div>
                              <div className="text-xs text-muted-foreground">Kar</div>
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      
                      <AccordionContent className="pb-3">
                        {/* Mobile-friendly table */}
                        <div className="hidden sm:block overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="hover:bg-transparent border-border/50">
                                <TableHead className="font-semibold text-xs">Kategori</TableHead>
                                <TableHead className="text-center font-semibold text-xs">Adet</TableHead>
                                <TableHead className="text-right font-semibold text-xs">İlk Maliyet</TableHead>
                                <TableHead className="text-right font-semibold text-xs">Güncel Maliyet</TableHead>
                                <TableHead className="text-right font-semibold text-xs">Satış Fiyatı</TableHead>
                                <TableHead className="text-right font-semibold text-xs">Kar</TableHead>
                                <TableHead className="text-right font-semibold text-xs">Nakit</TableHead>
                                <TableHead className="text-right font-semibold text-xs">Kredi Kartı</TableHead>
                                <TableHead className="text-right font-semibold text-xs">Senet</TableHead>
                                <TableHead className="text-right font-semibold text-xs">Ödero</TableHead>
                                <TableHead className="text-right font-semibold text-xs">Paro</TableHead>
                                <TableHead className="text-center font-semibold text-xs">Detay</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {dayData.records.map((record) => (
                                <TableRow key={record.id} className="hover:bg-muted/30 border-border/30">
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline" className="font-medium text-xs">
                                        {record.kategori}
                                      </Badge>
                                      {record.status === 'open' && (
                                        <Badge variant="outline" className="text-xs bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
                                          Beklemede
                                        </Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-center font-tabular text-xs">
                                    {record.adet}
                                  </TableCell>
                                  <TableCell className="text-right font-tabular text-xs">
                                    {formatCurrency(record.ilkMaliyet)}
                                  </TableCell>
                                  <TableCell className="text-right font-tabular text-xs">
                                    {formatCurrency(record.guncelMaliyet)}
                                  </TableCell>
                                  <TableCell className="text-right font-tabular font-semibold text-xs">
                                    {formatCurrency(record.satisFiyat)}
                                  </TableCell>
                                  <TableCell className="text-right font-tabular text-xs">
                                    <span className={record.kar > 0 ? 'text-success' : 'text-destructive'}>
                                      {formatCurrency(record.kar)}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-right font-tabular text-xs">
                                    {record.nakit > 0 ? formatCurrency(record.nakit) : '-'}
                                  </TableCell>
                                  <TableCell className="text-right font-tabular text-xs">
                                    {record.krediKarti > 0 ? formatCurrency(record.krediKarti) : '-'}
                                  </TableCell>
                                  <TableCell className="text-right font-tabular text-xs">
                                    {record.senet > 0 ? formatCurrency(record.senet) : '-'}
                                  </TableCell>
                                  <TableCell className="text-right font-tabular text-xs">
                                    {record.odero > 0 ? formatCurrency(record.odero) : '-'}
                                  </TableCell>
                                  <TableCell className="text-right font-tabular text-xs">
                                    {record.paro > 0 ? formatCurrency(record.paro) : '-'}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                      <Eye className="h-3 w-3" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>

                        {/* Mobile card layout */}
                        <div className="sm:hidden space-y-3">
                          {dayData.records.map((record) => (
                            <div key={record.id} className="glass-panel rounded-lg p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="font-medium text-xs">
                                    {record.kategori}
                                  </Badge>
                                  {record.status === 'open' && (
                                    <Badge variant="outline" className="text-xs bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
                                      Beklemede
                                    </Badge>
                                  )}
                                </div>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                  <Eye className="h-3 w-3" />
                                </Button>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Adet:</span>
                                  <span className="font-medium">{record.adet}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Satış:</span>
                                  <span className="font-medium">{formatCurrency(record.satisFiyat)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">İlk Maliyet:</span>
                                  <span>{formatCurrency(record.ilkMaliyet)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Kar:</span>
                                  <span className={record.kar > 0 ? 'text-success font-medium' : 'text-destructive'}>
                                    {formatCurrency(record.kar)}
                                  </span>
                                </div>
                              </div>
                              
                              {(record.nakit > 0 || record.krediKarti > 0 || record.senet > 0 || record.odero > 0 || record.paro > 0) && (
                                <div className="border-t pt-2">
                                  <div className="text-xs text-muted-foreground mb-1">Ödeme Yöntemleri:</div>
                                  <div className="grid grid-cols-2 gap-1 text-xs">
                                    {record.nakit > 0 && (
                                      <div className="flex justify-between">
                                        <span>Nakit:</span>
                                        <span>{formatCurrency(record.nakit)}</span>
                                      </div>
                                    )}
                                    {record.krediKarti > 0 && (
                                      <div className="flex justify-between">
                                        <span>Kredi Kartı:</span>
                                        <span>{formatCurrency(record.krediKarti)}</span>
                                      </div>
                                    )}
                                    {record.senet > 0 && (
                                      <div className="flex justify-between">
                                        <span>Senet:</span>
                                        <span>{formatCurrency(record.senet)}</span>
                                      </div>
                                    )}
                                    {record.odero > 0 && (
                                      <div className="flex justify-between">
                                        <span>Ödero:</span>
                                        <span>{formatCurrency(record.odero)}</span>
                                      </div>
                                    )}
                                    {record.paro > 0 && (
                                      <div className="flex justify-between">
                                        <span>Paro:</span>
                                        <span>{formatCurrency(record.paro)}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
};